import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '../lib/auth-client';
import { deriveOrganizationSlug, organizationNameWarning } from '../lib/create-org';
import { postCreateOrganizationPath } from '../lib/onboarding-flow';
import { AuthCard, Field, inputClass, primaryButtonClass, ErrorBanner, NoticeBanner } from '../components/AuthCard';

export default function CreateOrg() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const nameWarning = organizationNameWarning(name);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const slug = deriveOrganizationSlug(name);
    if (!slug) {
      setError(organizationNameWarning(name) || 'Enter a valid organization name.');
      return;
    }
    setSubmitting(true);
    const { error: err } = await authClient.organization.create({
      name: name.trim(),
      slug,
    });
    setSubmitting(false);
    if (err) {
      setError(err.message || 'Could not create organization.');
      return;
    }
    navigate(postCreateOrganizationPath(params.get('next')), { replace: true });
  }

  return (
    <AuthCard
      title="Create your organization"
      subtitle="Name the shared workspace where communication records and follow-up context will live."
    >
      {params.get('verified') === '1' && (
        <NoticeBanner tone="emerald" className="mb-4">
          Email verified. Create your organization to finish setup.
        </NoticeBanner>
      )}
      <ErrorBanner message={error} />
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Organization name" error={nameWarning || undefined}>
          <input
            type="text"
            required
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Acme Landscaping"
            className={inputClass}
          />
        </Field>
        <button type="submit" disabled={submitting || !name.trim() || Boolean(nameWarning)} className={primaryButtonClass}>
          {submitting ? 'Creating…' : 'Create organization'}
        </button>
      </form>
    </AuthCard>
  );
}
